import React, { useState, useRef, useMemo, useCallback } from 'react'
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Modal, Platform } from 'react-native'
import { corporateColor, textColor } from '../../../theme/colors'
import { DOBRA, DOBRA_BOLD } from '../../../theme/fonts'
import { CenterCategoryOneType, CenterInfo, JobListItem } from '../../../navigator/types'
import { EyeIcon } from './EyeIcon'

interface WorkSummaryProps {
  centersCategoryOne: CenterCategoryOneType
  boardingName?: string
  date?: string
}

interface ShiftGroup {
  shift: string
  items: Array<{
    centerId: number
    centerName: string
    jobName: string
    choreName: string
    percentage: number
  }>
}

export const WorkSummary = React.memo(
  ({ centersCategoryOne, boardingName, date }: WorkSummaryProps) => {
    const [showObservations, setShowObservations] = useState(false)
    const [observationData, setObservationData] = useState<{
      centerName: string
      observation: string
    } | null>(null)
    const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0, width: 0, height: 0 })
    const eyeContainerRefs = useRef<{ [key: string]: View }>({})

    const handleViewObservations = useCallback(
      (shift: string, centerName: string, observation: string) => {
        const ref = eyeContainerRefs.current[shift]
        if (Platform.OS === 'web') {
          // En web, usar posición fija centrada
          setObservationData({ centerName, observation })
          setShowObservations(true)
        } else if (ref) {
          ref.measureInWindow((x, y, width, height) => {
            setButtonPosition({ x, y, width, height })
            setObservationData({ centerName, observation })
            setShowObservations(true)
          })
        }
      },
      [],
    )

    const generateShiftGroups = useCallback((): ShiftGroup[] => {
      const shiftMap: { [key: string]: ShiftGroup } = {}

      Object.entries(centersCategoryOne).forEach(([_, centerInfo]: [string, CenterInfo]) => {
        const centerJobList = centerInfo.jobList

        const shiftsToUse = centerJobList ? Object.keys(centerJobList) : []
        shiftsToUse.forEach((shiftType) => {
          // Obtener trabajos específicos para este shift
          const shiftJobs = centerJobList?.[shiftType] ?? {}

          Object.entries(shiftJobs).forEach(([_, jobItem]: [string, JobListItem]) => {
            const item = {
              centerId: centerInfo.id,
              centerName: centerInfo.name,
              jobName: jobItem.jobName ?? 'Sin asignar',
              choreName: jobItem.choreName ?? 'Sin asignar',
              percentage: 0,
            }

            if (!shiftMap[shiftType]) {
              shiftMap[shiftType] = { shift: shiftType, items: [] }
            }
            shiftMap[shiftType]?.items.push(item)
          })
        })
      })

      return Object.values(shiftMap)
    }, [centersCategoryOne])

    const shiftGroups = useMemo(() => generateShiftGroups(), [generateShiftGroups])

    // Calcular si hay declaración de falla en algún centro
    const hasGlobalFailure = useMemo(() => {
      return Object.values(centersCategoryOne).some((centerInfo: CenterInfo) => {
        const failure = centerInfo?.failure
        return (
          failure && (failure.diver !== null || failure.ship !== null || failure.hydraulic !== null)
        )
      })
    }, [centersCategoryOne])

    const renderShiftGroup = useCallback(
      (group: ShiftGroup) => {
        const centerGroups: { [centerId: number]: typeof group.items } = {}
        group.items.forEach((item) => {
          if (!centerGroups[item.centerId]) {
            centerGroups[item.centerId] = []
          }
          centerGroups[item.centerId]?.push(item)
        })

        return (
          <View key={group.shift} style={styles.shiftSection}>
            <Text style={styles.shiftTitle}>Jornada de trabajo: {group.shift}</Text>

            {Object.entries(centerGroups).map(([centerId, centerItems]) => {
              const centerIdNum = Number(centerId)
              const centerInfo = centersCategoryOne[centerIdNum]
              const cecoValue = centerInfo?.ceco === 'CECO' ? 'CECO' : 'Código de apertura'
              const observation = centerInfo?.observation || 'Sin observaciones'

              return (
                <View key={`center-${centerId}`} style={styles.centerSubsection}>
                  <View style={styles.tableHeader}>
                    <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Centro</Text>
                    <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Imputar en</Text>
                    <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Trabajo</Text>
                    <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Faena</Text>
                  </View>

                  {centerItems.map((item, index) => (
                    <View key={`${centerId}-${index}`} style={styles.tableRow}>
                      <Text style={[styles.tableCell, { flex: 1.5 }]}>{item.centerName}</Text>
                      <Text style={[styles.tableCell, { flex: 1.5 }]}>{cecoValue}</Text>
                      <Text style={[styles.tableCell, { flex: 1.5 }]}>{item.jobName}</Text>
                      <Text style={[styles.tableCell, { flex: 1.5 }]}>{item.choreName}</Text>
                    </View>
                  ))}

                  <View style={styles.decalreFailureContainer}>
                    <TouchableOpacity
                      ref={(ref) => {
                        if (ref) eyeContainerRefs.current[`${centerId}`] = ref
                      }}
                      style={styles.eyeContainer}
                      onPress={() =>
                        handleViewObservations(
                          `${centerId}`,
                          centerItems[0]?.centerName ?? '',
                          observation,
                        )
                      }
                    >
                      <Text style={styles.viewObservationsLink}>ver observaciones</Text>
                      <EyeIcon />
                    </TouchableOpacity>
                  </View>
                </View>
              )
            })}
          </View>
        )
      },
      [centersCategoryOne, handleViewObservations],
    )

    return (
      <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
        <Text style={styles.mainTitle}>Resumen</Text>

        {boardingName && (
          <Text style={styles.boardingInfo}>
            Embarcación: {boardingName}
            {date ? ` • Día: ${date.split('-').reverse().join('-')}` : ''}
          </Text>
        )}

        {shiftGroups.length > 0 ? (
          shiftGroups.map((group) => renderShiftGroup(group))
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No hay trabajos registrados</Text>
          </View>
        )}

        {shiftGroups.length > 0 && (
          <View style={styles.globalFailureSection}>
            <Text style={styles.globalFailureLabel}>
              Declarar falla: {hasGlobalFailure ? 'SI' : 'NO'}
            </Text>
          </View>
        )}

        <Modal
          visible={showObservations}
          transparent={true}
          animationType='none'
          onRequestClose={() => setShowObservations(false)}
        >
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setShowObservations(false)}
          >
            <View
              style={[
                styles.popupContainer,
                Platform.OS === 'web'
                  ? styles.popupContainerWeb
                  : {
                      position: 'absolute',
                      top: buttonPosition.y + buttonPosition.height + 8,
                      right: Math.max(
                        16,
                        Math.min(buttonPosition.x + buttonPosition.width - 180, 16),
                      ),
                    },
              ]}
            >
              <Text style={styles.popupCenterTitle}>Centro: {observationData?.centerName}</Text>
              <Text style={styles.popupObservationText}>{observationData?.observation}</Text>
            </View>
          </TouchableOpacity>
        </Modal>
      </ScrollView>
    )
  },
)

WorkSummary.displayName = 'WorkSummary'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#fff',
  },
  scrollContent: {
    paddingBottom: 180,
  },
  mainTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: textColor,
    fontFamily: DOBRA,
    marginBottom: 12,
  },
  boardingInfo: {
    fontSize: 13,
    color: textColor,
    fontFamily: DOBRA,
    marginBottom: 20,
  },
  shiftSection: {
    marginBottom: 24,
  },
  shiftTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: textColor,
    fontFamily: DOBRA,
    marginBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingBottom: 8,
  },
  centerSubsection: {
    marginBottom: 16,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginBottom: 8,
  },
  tableHeaderCell: {
    fontSize: 11,
    fontWeight: '600',
    color: '#666',
    fontFamily: DOBRA,
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  tableCell: {
    fontSize: 12,
    color: textColor,
    fontFamily: DOBRA,
    textAlign: 'center',
  },
  decalreFailureContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 8,
    marginTop: 8,
  },
  eyeContainer: {
    flexDirection: 'row',
    gap: 4,
  },
  declareFailureLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: textColor,
    fontFamily: DOBRA,
  },
  viewObservationsLink: {
    fontSize: 12,
    color: corporateColor,
    fontFamily: DOBRA,
    fontWeight: '500',
  },
  emptyContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    fontSize: 14,
    color: textColor,
    fontFamily: DOBRA,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  popupContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingVertical: 16,
    paddingHorizontal: 20,
    width: 180,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    zIndex: 1000,
  },
  popupContainerWeb: {
    maxWidth: 320,
    width: '90%',
  },
  popupCenterTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: textColor,
    fontFamily: DOBRA_BOLD,
    marginBottom: 8,
  },
  popupObservationText: {
    fontSize: 14,
    color: textColor,
    lineHeight: 18,
    fontWeight: 'light',
  },
  globalFailureSection: {
    marginTop: 24,
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    marginBottom: 16,
  },
  globalFailureLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: textColor,
    fontFamily: DOBRA,
    textAlign: 'center',
  },
})
